import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, Inject, Injectable, ModuleWithProviders, NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { Attachments, Metadata } from '../models/share-point';
import { SaveButtonModule } from '../save-button/save-button';
import { SharePointService, SpGetOptions } from '../services/share-point';
import { UserService } from '../services/user';

export interface AppUpdatesConfig {
  updatesList?: string;
  userUpdatesList?: string;
  version: string;
}

export interface AppUpdate {
  Description: string;
  Title: string;
  Update: number;
  Attachments: Attachments;
  __metadata: Metadata;
}

export interface LastUpdateUserRecieved {
  LastVersion: number;
  __metadata?: Metadata;
}

interface AppUpdatesDialogData {
  version: number;
  updates: AppUpdate[];
}

@Component({
  selector: 'to-app-updates-modal',
  templateUrl: './app-updates-modal.component.html',
  styleUrls: ['./app-updates-modal.component.css']
})
export class AppUpdatesDialogComponent {
  saving = false;

  constructor(
    private appUpdates: AppUpdatesService,
    private dialogRef: MatDialogRef<AppUpdatesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AppUpdatesDialogData,
  ) { }

  complete() {
    if (!this.saving) {
      this.saving = true;
      this.appUpdates.recieved(this.data.version).subscribe(
        () => this.dialogRef.close(),
        () => this.saving = false,
        () => this.saving = false
      );
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class AppUpdatesService {
  private _userUpdatesList: string;
  private _updatesList: string;
  private _currentVersion: number;

  private _lastUpdateUserRecieved: LastUpdateUserRecieved;

  private _spGetOptions: SpGetOptions = {
    hideOnErrors: true,
    useLegacyApi: true,
    params: {
      $select: 'Title, Description, Attachments, Update',
      $expand: 'Attachments',
      $orderby: 'Update desc'
    }
  };

  constructor(
    @Inject('config') config: AppUpdatesConfig,
    private dialog: MatDialog,
    private spService: SharePointService,
    private userService: UserService,
  ) {
    this._userUpdatesList = config.userUpdatesList || 'Update_Notification';
    this._updatesList = config.updatesList || 'Updates';
    this._currentVersion = parseFloat(config.version) || 0;
  }

  get(): Observable<AppUpdate[]> {
    const fromVersion = this._lastUpdateUserRecieved.LastVersion || 0;

    if (Math.floor(fromVersion) !== Math.floor(this._currentVersion)) {
      this._spGetOptions.params.$filter = `Update ge ${Math.floor(this._currentVersion)}`;
    } else {
      this._spGetOptions.params.$filter = `Update gt ${fromVersion} and Update le ${this._currentVersion}`;
    }

    return this.spService.get<AppUpdate>(this._updatesList, this._spGetOptions);
  }

  recieved(version: number): Observable<LastUpdateUserRecieved> {
    this._lastUpdateUserRecieved.LastVersion = version;

    return this.spService.post<LastUpdateUserRecieved>(
      this._userUpdatesList,
      this._lastUpdateUserRecieved,
      {
        hideOnErrors: true,
      }
    );
  }

  checkUser(): Observable<AppUpdate[]> {

    return this.userService.current().pipe(
      filter(user => !!user && !!user.Id),
      switchMap(user => this.spService.get<LastUpdateUserRecieved>(this._userUpdatesList, {
        params: {
          $filter: `CreatedById eq ${user.Id}`,
          $select: 'LastVersion',
          $top: '1'
        },
        useLegacyApi: true,
      })),
      map(results => (results || [{ LastVersion: undefined }])[0] || { LastVersion: undefined }),
      map(data => {
        this._lastUpdateUserRecieved = data;
        return this._lastUpdateUserRecieved.LastVersion;
      }),
      filter(lastVersionUpdate => lastVersionUpdate !== this._currentVersion),
      switchMap(() => this.get()),
      tap(updates => {
        if (updates.length > 0) {
          this.dialog.open(AppUpdatesDialogComponent, {
            width: '800px',
            disableClose: true,
            autoFocus: false,
            data: {
              version: this._currentVersion,
              updates: updates
            }
          });
        } else {
          this.recieved(this._currentVersion).subscribe();
        }
      })
    );
  }
}

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    SaveButtonModule,
    MatDialogModule,
    MatStepperModule,
    MatButtonModule,
    FontAwesomeModule,
  ],
  declarations: [
    AppUpdatesDialogComponent
  ],
  entryComponents: [
    AppUpdatesDialogComponent
  ]
})
export class AppUpdatesModule {
  constructor(
    library: FaIconLibrary
  ) {
    library.addIcons(faCheck);
  }

  static forRoot(config: AppUpdatesConfig): ModuleWithProviders<AppUpdatesModule> {
    return {
      ngModule: AppUpdatesModule,
      providers: [AppUpdatesService, {
        provide: 'config',
        useValue: config
      }]
    };
  }
}
